import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { getLeague, setSnakeDraftOrder } from '../../data';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    Typography, Button, Paper, List, ListItem, ListItemText,
    IconButton, makeStyles, Container,
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 500,
        margin: '0 auto',
        padding: theme.spacing(3, 0),
    },
    header: {
        marginBottom: theme.spacing(2),
    },
    orderList: {
        marginBottom: theme.spacing(2),
    },
    listItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    rank: {
        fontWeight: 700,
        marginRight: theme.spacing(2),
        minWidth: 24,
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
}));

function SnakeDraftOrder() {
    const classes = useStyles();
    const { leagueID } = useParams();
    const history = useHistory();
    const [league, setLeagueData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [order, setOrder] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const data = await getLeague(leagueID);
                setLeagueData(data);
                // Use existing snakeDraftOrder if set, otherwise initialize from squad names
                const squadNames = (data.teams || []).map(t => t.name);
                if (data.snakeDraftOrder && data.snakeDraftOrder.length > 0) {
                    setOrder(data.snakeDraftOrder);
                } else {
                    setOrder(squadNames);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [leagueID]);

    const moveUp = (index) => {
        if (index === 0) return;
        const newOrder = [...order];
        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
        setOrder(newOrder);
    };

    const moveDown = (index) => {
        if (index === order.length - 1) return;
        const newOrder = [...order];
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        setOrder(newOrder);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setSaveError(null);
            await setSnakeDraftOrder(leagueID, order);
            history.push(`/leagues/${leagueID}/snake-draft`);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container style={{ textAlign: 'center', padding: 40 }}>
                <CircularProgress aria-label="Loading league" />
            </Container>
        );
    }

    if (error) {
        return (
            <Container style={{ textAlign: 'center', padding: 40 }}>
                <Typography color="error">{error}</Typography>
            </Container>
        );
    }

    return (
        <Container className={classes.root}>
            <div className={classes.header}>
                <Typography variant="h6">Set Snake Draft Order</Typography>
                <Typography variant="body2" color="textSecondary">
                    {league?.name} - Arrange the pick order for round 1. The draft will snake
                    (reverse direction each round).
                </Typography>
            </div>

            <Paper className={classes.orderList}>
                <List disablePadding>
                    {order.map((squadName, index) => (
                        <ListItem key={squadName} className={classes.listItem} divider>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Typography className={classes.rank}>{index + 1}.</Typography>
                                <ListItemText primary={squadName} />
                            </div>
                            <div>
                                <IconButton
                                    size="small"
                                    onClick={() => moveUp(index)}
                                    disabled={index === 0}
                                    aria-label={`Move ${squadName} up`}
                                >
                                    <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => moveDown(index)}
                                    disabled={index === order.length - 1}
                                    aria-label={`Move ${squadName} down`}
                                >
                                    <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                            </div>
                        </ListItem>
                    ))}
                </List>
            </Paper>

            {saveError && (
                <Typography color="error" style={{ marginBottom: 8 }}>{saveError}</Typography>
            )}

            <div className={classes.actions}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || order.length === 0}
                    data-testid="save-draft-order"
                >
                    {saving ? 'Saving...' : 'Save Order & Start Draft'}
                </Button>
                <Link to={`/leagues/${leagueID}`}>
                    <Button variant="outlined">Cancel</Button>
                </Link>
            </div>
        </Container>
    );
}

export default SnakeDraftOrder;
